import { calculate, LcaPayload } from "./lca";
import {
  BaseLogo,
  getColorKey,
  getLcaType,
  LcaType,
} from "../core/models/entities/BaseLogo";
import { Color } from "../core/models/entities/LogoOptions";
import { CbsConfig } from "../core/models/entities/CbsConfig";
import {
  Order,
  OrderLine,
  OrderLineLogo,
} from "../core/models/entities/Order";

export interface OrderCo2ePerLogo {
  logoId: number;
  logoName: string;
  logoSerial?: string;
  amount: number;
  contribution: number;
}

export interface OrderCo2eResult {
  total: number;
  perLogo: OrderCo2ePerLogo[];
}

// NOTE: uses base logo size (widthMm × heightMm), not per-placement design size.
// Accepted trade-off — see plan.
export function calculateOrderCo2e(
  order: Order,
  cbsConfig: CbsConfig[],
): OrderCo2eResult | null {
  try {
    const aggregated = aggregateLogosByQuantity(order.lines);
    const perLogo: OrderCo2ePerLogo[] = [];

    for (const entry of Array.from(aggregated.values())) {
      const payload = buildPayload(entry.logo, entry.quantity, cbsConfig);
      if (!payload) continue;

      const result = calculate(payload);
      if (!isFinite(result.VuggeTilPortTotal) || result.VuggeTilPortTotal < 0) continue;

      perLogo.push({
        logoId: entry.logo.id,
        logoName: entry.logo.title ?? `#${entry.logo.id}`,
        logoSerial: entry.logo.serial,
        amount: entry.quantity,
        contribution: result.VuggeTilPortTotal,
      });
    }

    if (perLogo.length === 0) return null;

    const total = perLogo.reduce((sum, p) => sum + p.contribution, 0);
    if (!isFinite(total) || total <= 0) return null;

    return { total, perLogo };
  } catch {
    return null;
  }
}

interface LogoEntry {
  logo: BaseLogo;
  quantity: number;
}

function aggregateLogosByQuantity(lines: OrderLine[]): Map<number, LogoEntry> {
  const map = new Map<number, LogoEntry>();

  const record = (logo: BaseLogo | undefined, qty: number) => {
    if (!logo || !logo.id || qty <= 0) return;
    const existing = map.get(logo.id);
    if (existing) {
      existing.quantity += qty;
    } else {
      map.set(logo.id, { logo, quantity: qty });
    }
  };

  // Standalone NumberLogo orders emit a parent "container" line plus one
  // OrderLine per typed digit (see useOrderActionsHandler emitLine in the
  // digit-split branch). The parent's quantity is the per-piece count (30
  // garments) while the digit lines together carry the per-application count
  // (40 prints for "1×10, 2×10, 12×10"). Counting both double-counts. The
  // parent is identified as a top-level Logo line (relatedTo === 0) that has
  // children pointing back to it. Regular ImageLogo/TextLogo standalone
  // orders also have relatedTo === 0 but no children, so they aren't skipped.
  // Design-instance NumberLogos emit a single line per typed value with the
  // digit multiplier already baked into quantity, and have relatedTo set to
  // the design line — they aren't affected.
  const parentLineIds = new Set<number>();
  for (const line of lines) {
    if (line.relatedTo) parentLineIds.add(line.relatedTo);
  }
  const isStandaloneParentContainer = (line: OrderLine): boolean =>
    line.relatedTo === 0 && parentLineIds.has(line.id);

  // Designs are intentionally NOT iterated here. buildLocalOrderDraft already
  // emits a productType="Logo" child line per design instance × logo placement
  // (with the NumberLogo digit multiplier baked into the quantity). Iterating
  // the Design line's designLogos as well would double-count each design logo
  // (e.g., a design with one logo at qty 10 would land at 20).
  for (const line of lines) {
    const productType = line.product?.productType;
    if (productType !== "Logo") continue;
    if (isStandaloneParentContainer(line)) continue;
    const logoLine = line as OrderLineLogo;
    record(logoLine.baseLogo, logoLine.quantity ?? 0);
  }

  return map;
}

function buildPayload(
  logo: BaseLogo,
  amount: number,
  cbsConfig: CbsConfig[],
): LcaPayload | null {
  const type = getLcaType(logo);
  if (!type) return null;

  const widthMm = logo.widthMm;
  const heightMm = logo.heightMm;
  if (!widthMm || !heightMm || widthMm <= 0 || heightMm <= 0) return null;

  const sizeCm2 = (widthMm / 10) * (heightMm / 10);
  if (!isFinite(sizeCm2) || sizeCm2 <= 0) return null;

  const allColors = logo.colorDetails ?? [];
  // Backup White is an underprint layer, not a visible color — exclude from count/classification.
  const colors = allColors.filter((c) => !c.isBackup);
  const colorCount = Math.max(1, colors.length);

  switch (type) {
    case "Broderi": {
      const stitches = logo.stitches ?? 0;
      if (stitches <= 0) return null;
      const stitchesPerCm2 = stitches / sizeCm2;
      if (!isFinite(stitchesPerCm2) || stitchesPerCm2 <= 0) return null;
      return {
        Type: "Broderi",
        Amount: amount,
        SizeCM2: sizeCm2,
        "Number of stitches per cm2": stitchesPerCm2,
        Avalon: true,
      };
    }
    case "Direkte Tryk":
      return {
        Type: "Direkte Tryk",
        Amount: amount,
        SizeCM2: sizeCm2,
        ColorCount: colorCount,
        DarkTextile: true,
      };
    case "Transfer":
    case "Digital Transfer": {
      const logosPerSheet = computeLogosPerSheet(widthMm, heightMm);
      if (!logosPerSheet) return null;
      // White underprint: logo contains a Backup White color.
      const white = allColors.some((c) => c.isBackup);
      const methodKey = getMethodKey(logo);
      const { lim, blokker } = lookupLimAndBlokker(
        colors,
        colorCount > 1,
        methodKey,
        cbsConfig,
      );
      return {
        Type: type,
        Amount: amount,
        SizeCM2: sizeCm2,
        ColorCount: colorCount,
        White: white,
        Blokker: blokker,
        Lim: lim,
        "Logos per sheet": logosPerSheet,
      };
    }
  }
}

function computeLogosPerSheet(widthMm: number, heightMm: number): number | null {
  const maxSide = Math.max(widthMm, heightMm);
  if (maxSide <= 100) return 8;
  if (maxSide <= 200) return 6;
  if (maxSide <= 300) return 2;
  if (maxSide <= 350) return 2;
  return null;
}

function getMethodKey(logo: BaseLogo): string | null {
  const m = logo.transferMethod;
  if (!m) return null;
  // CbsConfig.MethodKey stores the raw API method Key (e.g. "TRANSFER_VAND").
  // Backend splits this into Type + Key (where spaces become commas) — we rebuild it here.
  const itemType = m.type?.toUpperCase();
  const key = m.key?.replace(/ /g, ",");
  if (!itemType || !key) return null;
  return `${itemType}_${key}`;
}

function lookupLimAndBlokker(
  colors: Color[],
  isColor: boolean,
  methodKey: string | null,
  cbsConfig: CbsConfig[],
): { lim: boolean; blokker: boolean } {
  if (!methodKey || cbsConfig.length === 0) {
    return { lim: false, blokker: false };
  }

  let lim = false;
  let blokker = false;

  const relevantColorKeys = Array.from(
    new Set(colors.map((c) => getColorKey(c))),
  );

  for (const colorKey of relevantColorKeys) {
    const entry = cbsConfig.find(
      (cc) =>
        cc.methodKey === methodKey &&
        cc.isColor === isColor &&
        cc.colorKey === colorKey,
    );
    if (!entry) continue;
    for (const tc of entry.typeColors) {
      const k = tc.key?.toUpperCase();
      if (k === "GLUE") lim = true;
      if (k === "BLOKKER") blokker = true;
    }
  }

  return { lim, blokker };
}

// Re-export for unused-type warning suppression on some build configs.
export type { LcaType };
